import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, MailIcon, BarChart3Icon, CheckCircle2Icon, CheckIcon, SendIcon } from 'lucide-react';
type Step = 'search' | 'create' | 'track';
export function SequenceDemo() {
  const [activeStep, setActiveStep] = useState<Step>('search');
  const [searchPhase, setSearchPhase] = useState<'loading' | 'done'>('loading');
  const [createPhase, setCreatePhase] = useState<'loading' | 'done'>('loading');
  const [trackPhase, setTrackPhase] = useState<'loading' | 'done'>('loading');
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const createTimerRef = useRef<NodeJS.Timeout | null>(null);
  const trackTimerRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    startSearch();
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (createTimerRef.current) clearTimeout(createTimerRef.current);
      if (trackTimerRef.current) clearTimeout(trackTimerRef.current);
    };
  }, []);
  const startSearch = () => {
    setSearchPhase('loading');
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setSearchPhase('done'), 1200);
  };
  const startCreate = () => {
    setCreatePhase('loading');
    if (createTimerRef.current) clearTimeout(createTimerRef.current);
    createTimerRef.current = setTimeout(() => setCreatePhase('done'), 1400);
  };
  const startTrack = () => {
    setTrackPhase('loading');
    if (trackTimerRef.current) clearTimeout(trackTimerRef.current);
    trackTimerRef.current = setTimeout(() => setTrackPhase('done'), 1500);
  };
  const handleTabSwitch = (step: Step) => {
    setActiveStep(step);
    if (step === 'search') startSearch();
    if (step === 'create') startCreate();
    if (step === 'track') startTrack();
  };
  return <section className="py-24 bg-white" data-id="element-794">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-795">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-796">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-797">
            See <span className="text-blue-600" data-id="element-798">email sequencing</span> in
            action
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-799">
            Find leads, build B2B email sequences, and track results — all from
            one platform.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-14" data-id="element-800">
          <button onClick={() => handleTabSwitch('search')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'search' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-801">
            <SearchIcon className="w-4 h-4" data-id="element-802" />
            Build prospect list
          </button>
          <button onClick={() => handleTabSwitch('create')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'create' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-803">
            <MailIcon className="w-4 h-4" data-id="element-804" />
            Create sequence
          </button>
          <button onClick={() => handleTabSwitch('track')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'track' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-805">
            <BarChart3Icon className="w-4 h-4" data-id="element-806" />
            Track results
          </button>
        </div>

        <AnimatePresence mode="wait" data-id="element-807">
          {activeStep === 'search' && <motion.div key="search" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-808">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-809">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-810">
                  Search filters
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-811">
                  Find local businesses to target.
                </p>
                <div className="space-y-5" data-id="element-812">
                  <div data-id="element-813">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-814">
                      Industry
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-815">
                      Restaurants & Cafes
                    </div>
                  </div>
                  <div data-id="element-816">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-817">
                      Location
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-818">
                      Austin, TX
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-819">
                  {searchPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-820">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-821" />
                      Searching database...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-822">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-823" />
                      Found 842 prospects
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-824">
                {searchPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-825">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-826" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-827">
                      Finding local business leads...
                    </p>
                  </div>}
                {searchPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-828">
                    <div className="flex items-center justify-between mb-6" data-id="element-829">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-830">
                        Prospect list ready
                      </h3>
                    </div>
                    <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-id="element-831">
                      {[{
                  name: "Joe's Pizza",
                  owner: 'Joe Martinez',
                  email: 'joe@joespizza.com',
                  phone: '(512) 555-0134',
                  location: 'Austin, TX',
                  employees: '12',
                  revenue: '$1.8M'
                }, {
                  name: 'Austin Coffee Co',
                  owner: 'Sarah Chen',
                  email: 'sarah@austincoffee.com',
                  phone: '(512) 555-0267',
                  location: 'Austin, TX',
                  employees: '8',
                  revenue: '$950K'
                }, {
                  name: 'Texas BBQ Joint',
                  owner: 'Marcus Williams',
                  email: 'marcus@texasbbq.com',
                  phone: '(512) 555-0391',
                  location: 'Round Rock, TX',
                  employees: '22',
                  revenue: '$2.4M'
                }, {
                  name: 'Lone Star Tacos',
                  owner: 'Maria Gonzalez',
                  email: 'maria@lonestartacos.com',
                  phone: '(512) 555-0478',
                  location: 'Austin, TX',
                  employees: '15',
                  revenue: '$1.2M'
                }, {
                  name: 'Hill Country Bakery',
                  owner: 'Emily Brooks',
                  email: 'emily@hillcountrybakery.com',
                  phone: '(512) 555-0512',
                  location: 'Cedar Park, TX',
                  employees: '6',
                  revenue: '$680K'
                }, {
                  name: 'Capital City Sushi',
                  owner: 'David Tanaka',
                  email: 'david@capitalcitysushi.com',
                  phone: '(512) 555-0645',
                  location: 'Austin, TX',
                  employees: '18',
                  revenue: '$2.1M'
                }, {
                  name: 'Riverside Diner',
                  owner: 'Tom Henderson',
                  email: 'tom@riversidediner.com',
                  phone: '(512) 555-0723',
                  location: 'Pflugerville, TX',
                  employees: '10',
                  revenue: '$1.1M'
                }].map((prospect, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.08,
                  duration: 0.3
                }} className="bg-white rounded-xl p-4 shadow-sm" data-id="element-832">
                          <div className="flex items-start justify-between mb-2" data-id="element-833">
                            <div data-id="element-834">
                              <h4 className="text-sm font-bold text-slate-900" data-id="element-835">
                                {prospect.name}
                              </h4>
                              <p className="text-xs text-slate-500 mt-0.5" data-id="element-836">
                                {prospect.owner} · {prospect.location}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 bg-green-50 text-green-700 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase shrink-0" data-id="element-837">
                              <CheckIcon className="w-3 h-3" data-id="element-838" /> Verified
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500" data-id="element-839">
                            <span className="flex items-center gap-1" data-id="element-840">
                              <MailIcon className="w-3 h-3 text-blue-400" data-id="element-841" />
                              {prospect.email}
                            </span>
                            <span data-id="element-842">{prospect.phone}</span>
                            <span className="text-slate-300" data-id="element-843">·</span>
                            <span data-id="element-844">{prospect.employees} employees</span>
                            <span className="text-slate-300" data-id="element-845">·</span>
                            <span data-id="element-846">{prospect.revenue} rev</span>
                          </div>
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'create' && <motion.div key="create" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-847">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-848">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-849">
                  Sequence settings
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-850">
                  Configure your email sequence tools.
                </p>
                <div className="space-y-3" data-id="element-851">
                  <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-100" data-id="element-852">
                    <span className="text-sm font-medium text-slate-700" data-id="element-853">
                      Step 1: Initial Outreach
                    </span>
                    <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded" data-id="element-854">
                      Day 1
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-100" data-id="element-855">
                    <span className="text-sm font-medium text-slate-700" data-id="element-856">
                      Step 2: First Follow-up
                    </span>
                    <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded" data-id="element-857">
                      Day 4
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-100" data-id="element-858">
                    <span className="text-sm font-medium text-slate-700" data-id="element-859">
                      Step 3: Second Follow-up
                    </span>
                    <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded" data-id="element-860">
                      Day 7
                    </span>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-861">
                  {createPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-862">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-863" />
                      Generating previews...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-864">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-865" />
                      Sequence ready
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-866">
                {createPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-867">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-868" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-869">
                      Applying personalization variables...
                    </p>
                  </div>}
                {createPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-870">
                    <div className="flex items-center justify-between mb-6" data-id="element-871">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-872">
                        Email preview
                      </h3>
                    </div>
                    <div className="space-y-4" data-id="element-873">
                      <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.15,
                  duration: 0.3
                }} className="bg-white rounded-2xl p-6 shadow-sm border border-blue-100" data-id="element-874">
                        <div className="mb-4 pb-4 border-b border-slate-100" data-id="element-875">
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-876">
                            Subject
                          </div>
                          <div className="text-sm font-semibold text-slate-900" data-id="element-877">
                            Question about Joe's Pizza
                          </div>
                        </div>
                        <div data-id="element-878">
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-879">
                            Body
                          </div>
                          <div className="text-sm text-slate-700 leading-relaxed" data-id="element-880">
                            Hi Joe,
                            <br data-id="element-881" />
                            <br data-id="element-882" />
                            I noticed that Joe's Pizza is currently using Toast
                            POS in Austin, TX.
                            <br data-id="element-883" />
                            <br data-id="element-884" />
                            We help restaurants like yours streamline
                            operations. Open to a quick chat?
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'track' && <motion.div key="track" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-885">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-886">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-887">
                  Campaign stats
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-888">
                  Monitor your B2B email sequence.
                </p>
                <div className="space-y-5" data-id="element-889">
                  <div data-id="element-890">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-891">
                      Enrolled prospects
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700 font-bold" data-id="element-892">
                      842
                    </div>
                  </div>
                  <div data-id="element-893">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-894">
                      CRM Sync
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-green-600 font-bold flex items-center gap-2 border-2 border-green-100" data-id="element-895">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-896" /> Active
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-897">
                  {trackPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-898">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-899" />
                      Fetching analytics...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-900">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-901" />
                      Live data loaded
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-902">
                {trackPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-903">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-904" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-905">
                      Loading sequence performance...
                    </p>
                  </div>}
                {trackPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-906">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8" data-id="element-907">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-908">
                        Sequence performance
                      </h3>
                      <div className="flex items-center gap-1.5 bg-green-50 text-green-700 px-2.5 py-1 rounded-full text-xs font-bold" data-id="element-909">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-910" />
                        Live
                      </div>
                    </div>

                    {/* 3 Key Metrics */}
                    <div className="grid grid-cols-3 gap-3 mb-8" data-id="element-911">
                      {[{
                  label: 'Emails sent',
                  value: '3,540',
                  icon: SendIcon
                }, {
                  label: 'Open rate',
                  value: '42.8%',
                  icon: MailIcon
                }, {
                  label: 'Reply rate',
                  value: '12.4%',
                  icon: CheckCircle2Icon
                }].map((m, i) => {
                  const MIcon = m.icon;
                  return <motion.div key={i} initial={{
                    opacity: 0,
                    y: 12
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: i * 0.1,
                    duration: 0.4,
                    ease: 'easeOut'
                  }} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 text-center" data-id="element-912">
                            <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-3" data-id="element-913">
                              <MIcon className="w-4 h-4 text-blue-600" data-id="element-914" />
                            </div>
                            <div className="text-2xl font-bold text-slate-900" data-id="element-915">
                              {m.value}
                            </div>
                            <div className="text-xs text-slate-400 mt-1 font-medium" data-id="element-916">
                              {m.label}
                            </div>
                          </motion.div>;
                })}
                    </div>

                    {/* Simple Funnel Bars */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6" data-id="element-917">
                      <div className="text-xs font-semibold text-slate-700 mb-5" data-id="element-918">
                        Engagement funnel
                      </div>
                      <div className="space-y-4" data-id="element-919">
                        {[{
                    label: 'Sent',
                    value: 3540,
                    pct: 100,
                    color: 'bg-slate-800'
                  }, {
                    label: 'Opened',
                    value: 1515,
                    pct: 42.8,
                    color: 'bg-blue-500'
                  }, {
                    label: 'Clicked',
                    value: 648,
                    pct: 18.3,
                    color: 'bg-blue-400'
                  }, {
                    label: 'Replied',
                    value: 439,
                    pct: 12.4,
                    color: 'bg-emerald-500'
                  }].map((step, i) => <motion.div key={i} initial={{
                    opacity: 0,
                    x: -10
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: 0.2 + i * 0.1,
                    duration: 0.35,
                    ease: 'easeOut'
                  }} data-id="element-920">
                            <div className="flex items-center justify-between mb-1.5" data-id="element-921">
                              <span className="text-sm font-medium text-slate-700" data-id="element-922">
                                {step.label}
                              </span>
                              <span className="text-sm font-bold text-slate-900" data-id="element-923">
                                {step.value.toLocaleString()}
                                <span className="text-xs font-normal text-slate-400 ml-1" data-id="element-924">
                                  ({step.pct}%)
                                </span>
                              </span>
                            </div>
                            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-925">
                              <motion.div initial={{
                        width: 0
                      }} animate={{
                        width: `${step.pct}%`
                      }} transition={{
                        delay: 0.3 + i * 0.12,
                        duration: 0.6,
                        ease: 'easeOut'
                      }} className={`h-full rounded-full ${step.color}`} data-id="element-926" />
                            </div>
                          </motion.div>)}
                      </div>
                    </div>

                    {/* CTA */}
                    <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="w-full px-6 py-3.5 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700 transition-colors text-base text-center block" data-id="element-927">
                      Start your first sequence
                    </a>
                  </motion.div>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}